import {IntentRequest, LoginRequest, LoginResponse, MonthDetails, PocketDetails, PocketRequest, TodayDetails, TransactionRequest} from "../logic/types";
import {useAuthContext} from "../logic/AuthContext";
import {loadAccessToken, saveAccessToken} from "../logic/helpers";
import {useNavigate} from "react-router-dom";
import {toLocalDateString, toYearMonthString} from "../logic/date";

// const apiBaseURL = 'https://money-back-ec2.katze.link/api'
// const apiBaseURL = 'http://localhost:8082'
const apiBaseURL = 'https://life-platform.katze.link'
const apiUsersBaseURL = apiBaseURL + '/users/v1.0'
const apiMoneyBaseURL = apiBaseURL + '/moneytrack/v1.0'
const apiTransactionsURL = apiMoneyBaseURL + '/transactions'
const apiIntentsURL = apiMoneyBaseURL + '/intents'
const apiPocketsURL = apiMoneyBaseURL + '/pockets'
const apiMonthDetailsURL = apiMoneyBaseURL + '/month-details'
const apiTodayDetailsURL = apiMoneyBaseURL + '/today-details'

export interface LifePlatformApiClient {
  apiLogin(request: LoginRequest): Promise<LoginResponse | null>,

  apiFetchMonthDetails(month: Date): Promise<MonthDetails | null>,

  apiFetchTodayDetails(date: Date): Promise<TodayDetails | null>,

  apiAddTransaction(request: TransactionRequest): Promise<boolean>,

  apiChangeTransaction(transactionID: string, request: TransactionRequest): Promise<boolean>,

  apiDeleteTransaction(transactionID: string): Promise<boolean>,

  apiAddIntent(request: IntentRequest): Promise<boolean>,

  apiChangeIntent(intentID: string, request: IntentRequest): Promise<boolean>,

  apiDeleteIntent(intentID: string, date: Date): Promise<boolean>,

  apiIgnoreIntent(intentID: string, month: Date): Promise<boolean>,

  apiAddPocket(request: PocketRequest): Promise<boolean>,

  apiChangePocket(pocketID: string, request: PocketRequest): Promise<boolean>,

  apiDeletePocket(pocketID: string): Promise<boolean>,

  apiFetchPocketDetails(pocketID: string, month: Date): Promise<PocketDetails | null>,
}

export function useLifePlatformApiClient(): LifePlatformApiClient {

  const {setIsAuthenticated} = useAuthContext()
  let navigate = useNavigate();

  async function apiLogin(request: LoginRequest): Promise<LoginResponse | null> {
    // await new Promise(r => setTimeout(r, 2000))
    try {
      const serverResponse = await fetch(apiUsersBaseURL + '/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json;charset=utf-8',
        },
        body: JSON.stringify(request)
      })

      if (serverResponse.ok) {
        const response = await serverResponse.json() as LoginResponse
        saveAccessToken(response.accessToken)
        setIsAuthenticated(true)
        console.log(`login ok: access allowed`)
        return response
      } else if (serverResponse.status === 401) {
        alert('емейл или пароль неверны')
      } else {
        alert(`login Ошибка HTTP: ${serverResponse.status}: ${serverResponse.statusText}`)
      }
    } catch (err) {
      alert(`login error Ошибка: ${err}`)
    }

    return null
  }

  async function apiFetchMonthDetails(month: Date): Promise<MonthDetails | null> {
    const url = `${apiMonthDetailsURL}?month=${toYearMonthString(month)}`
    return await makeRequest<MonthDetails>(url, true);
  }

  async function apiFetchTodayDetails(date: Date): Promise<TodayDetails | null> {
    const url = `${apiTodayDetailsURL}?date=${toLocalDateString(date)}`
    return await makeRequest<TodayDetails>(url, true);
  }

  async function apiAddTransaction(request: TransactionRequest): Promise<boolean> {
    return await makeRequest<boolean>(apiTransactionsURL, false, request, {method: 'POST'}) ?? false;
  }

  async function apiChangeTransaction(transactionID: string, request: TransactionRequest): Promise<boolean> {
    const url = apiTransactionsURL + '/' + transactionID
    return await makeRequest<boolean>(url, false, request, {method: 'PATCH'}) ?? false;
  }

  async function apiDeleteTransaction(transactionID: string): Promise<boolean> {
    const url = apiTransactionsURL + '/' + transactionID
    return await makeRequest<boolean>(url, false, undefined, {method: 'DELETE'}) ?? false;
  }

  async function apiAddIntent(request: IntentRequest): Promise<boolean> {
    return await makeRequest<boolean>(apiIntentsURL, false, request, {method: 'POST'}) ?? false;
  }

  async function apiChangeIntent(intentID: string, request: IntentRequest): Promise<boolean> {
    const url = apiIntentsURL + '/' + intentID
    return await makeRequest<boolean>(url, false, request, {method: 'PATCH'}) ?? false;
  }

  async function apiDeleteIntent(intentID: string, date: Date): Promise<boolean> {
    const url = apiIntentsURL + '/' + intentID + `?month=${toYearMonthString(date)}`
    return await makeRequest<boolean>(url, false, undefined, {method: 'DELETE'}) ?? false;
  }

  async function apiIgnoreIntent(intentID: string, month: Date): Promise<boolean> {
    const url = apiIntentsURL + '/' + intentID + `/ignore?month=${toYearMonthString(month)}`
    return await makeRequest<boolean>(url, false, undefined, {method: 'POST'}) ?? false;
  }

  async function apiAddPocket(request: PocketRequest): Promise<boolean> {
    return await makeRequest<boolean>(apiPocketsURL, false, request, {method: 'POST'}) ?? false;
  }

  async function apiChangePocket(pocketID: string, request: PocketRequest): Promise<boolean> {
    const url = apiPocketsURL + '/' + pocketID
    return await makeRequest<boolean>(url, false, request, {method: 'PATCH'}) ?? false;
  }

  async function apiDeletePocket(pocketID: string): Promise<boolean> {
    const url = apiPocketsURL + '/' + pocketID
    return await makeRequest<boolean>(url, false, undefined, {method: 'DELETE'}) ?? false;
  }

  async function apiFetchPocketDetails(pocketID: string, month: Date): Promise<PocketDetails | null> {
    const url = `${apiPocketsURL}/${pocketID}?month=${toYearMonthString(month)}`
    return await makeRequest<PocketDetails>(url, true);
  }

  async function makeRequest<ResponseBody>(url: string, hasResponseBody: boolean = false, requestBody: any = undefined, options: RequestInit = {}): Promise<ResponseBody | null> {
    try {
      const accessToken = loadAccessToken()
      if (!accessToken) {
        alert("Отсутсвует accessToken")
        resetAuthentication();
        return null
      }

      options.headers = {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`,
      };

      if (requestBody) {
        options.body = JSON.stringify(requestBody);
        options.headers = {
          ...options.headers,
          'Content-Type': 'application/json;charset=utf-8',
        };
      }

      const response = await fetch(url, options);
      if (response.ok) {
        if (hasResponseBody) {
          return await response.json() as ResponseBody;
        } else {
          // @ts-ignore
          return true;
        }
      } else {
        if (response.status === 401) {
          // Unauthorized, redirect to apiLogin
          resetAuthentication();
        }
        alert(`Ошибка HTTP! Статус код: ${response.status}`);
      }

      if (hasResponseBody) {
        // @ts-ignore
        return false
      } else {
        return null
      }
    } catch (err) {
      alert(`Программная ошибка: ${err}`)
      if (hasResponseBody) {
        // @ts-ignore
        return false
      } else {
        return null
      }
    }
  }

  function resetAuthentication() {
    setIsAuthenticated(false);
    navigate("/login");
  }

  return {
    apiLogin: apiLogin,
    apiFetchMonthDetails: apiFetchMonthDetails,
    apiFetchTodayDetails: apiFetchTodayDetails,
    apiAddTransaction: apiAddTransaction,
    apiChangeTransaction: apiChangeTransaction,
    apiDeleteTransaction: apiDeleteTransaction,
    apiAddIntent: apiAddIntent,
    apiChangeIntent: apiChangeIntent,
    apiDeleteIntent: apiDeleteIntent,
    apiIgnoreIntent: apiIgnoreIntent,
    apiAddPocket: apiAddPocket,
    apiChangePocket: apiChangePocket,
    apiDeletePocket: apiDeletePocket,
    apiFetchPocketDetails: apiFetchPocketDetails,
  };
}
